import { useEffect, useState } from "react";
import { api, money, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Plus, Trash2, Users, CalendarDays, Save } from "lucide-react";

const empty = {
  title: "", description: "", city: "", region: "", venue: "",
  start_date: "", end_date: "", daily_time: "09:00", capacity: 12, price: 349,
  includes: [], levels: [], status: "draft",
};

const input = "mt-1.5 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200";

export const CampsManager = () => {
  const [camps, setCamps] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/coach/camps").then((r) => setCamps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      capacity: Number(form.capacity), price: Number(form.price),
      includes: Array.isArray(form.includes) ? form.includes : String(form.includes).split(",").map((s) => s.trim()).filter(Boolean),
      levels: Array.isArray(form.levels) ? form.levels : String(form.levels).split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) await api.put(`/coach/camps/${editingId}`, payload);
      else await api.post("/coach/camps", payload);
      toast.success(editingId ? "Camp updated" : "Camp created");
      setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const publish = async (camp, status) => {
    try {
      await api.put(`/coach/camps/${camp.id}`, { ...camp, status });
      toast.success(status === "published" ? "Camp is live" : "Camp unpublished");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (camp) => {
    try { await api.delete(`/coach/camps/${camp.id}`); toast.success("Camp deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-white/10 bg-[#121212] p-6 space-y-4">
        <h2 className="display text-xl uppercase tracking-tight font-bold">{editingId ? "Edit camp" : "New camp"}</h2>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Title</span>
          <input data-testid="camp-title-input" className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Description</span>
          <textarea data-testid="camp-description-input" rows={3} className={input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
        <div className="grid grid-cols-2 gap-4">
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">City</span>
            <input data-testid="camp-city-input" className={input} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Region</span>
            <input data-testid="camp-region-input" className={input} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Venue / rink</span>
            <input data-testid="camp-venue-input" className={input} value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Daily start time</span>
            <input data-testid="camp-time-input" className={input} value={form.daily_time} onChange={(e) => setForm({ ...form, daily_time: e.target.value })} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Start date</span>
            <input data-testid="camp-start-input" type="date" className={input} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">End date</span>
            <input data-testid="camp-end-input" type="date" className={input} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Capacity</span>
            <input data-testid="camp-capacity-input" type="number" className={input} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Price per camper ($)</span>
            <input data-testid="camp-price-input" type="number" className={input} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></label>
        </div>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">What's included (comma separated)</span>
          <input data-testid="camp-includes-input" className={input}
            value={Array.isArray(form.includes) ? form.includes.join(", ") : form.includes}
            onChange={(e) => setForm({ ...form, includes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Levels (comma separated)</span>
          <input data-testid="camp-levels-input" className={input}
            value={Array.isArray(form.levels) ? form.levels.join(", ") : form.levels}
            onChange={(e) => setForm({ ...form, levels: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
        <div className="flex gap-3">
          <button data-testid="camp-save-btn" onClick={save}
            className="bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-6 py-3 flex items-center gap-2 hover:bg-[#00b3cc] transition-colors duration-200">
            <Save className="w-4 h-4" /> {editingId ? "Save camp" : "Create camp"}
          </button>
          {editingId && (
            <button data-testid="camp-cancel-edit-btn" onClick={() => { setForm(empty); setEditingId(null); }}
              className="border border-white/15 text-xs uppercase text-zinc-400 px-5 hover:border-white/40 transition-colors duration-200">Cancel</button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {camps.length === 0 && <p data-testid="coach-no-camps" className="border border-white/10 p-10 text-sm text-zinc-500">No camps yet — create your first one.</p>}
        {camps.map((c) => (
          <div key={c.id} data-testid={`coach-camp-${c.id}`} className="border border-white/10 bg-[#121212] p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="display uppercase font-bold text-lg leading-none">{c.title}</p>
                <p className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {c.start_date} → {c.end_date} · {c.city}</p>
                <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {c.spots_taken}/{c.capacity} booked · {money(c.price)} each</p>
              </div>
              <span className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${c.status === "published" ? "border-[#00e5ff]/50 text-[#00e5ff]" : "border-white/20 text-zinc-400"}`}>{c.status}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <button data-testid={`camp-edit-${c.id}`} onClick={() => { setForm(c); setEditingId(c.id); }}
                className="text-[10px] uppercase tracking-widest border border-white/15 text-zinc-300 px-3 py-2 hover:border-white/40 transition-colors duration-200">Edit</button>
              <button data-testid={`camp-publish-${c.id}`} onClick={() => publish(c, c.status === "published" ? "draft" : "published")}
                className="text-[10px] uppercase tracking-widest border border-[#00e5ff] text-[#00e5ff] px-3 py-2 hover:bg-[#00e5ff] hover:text-black transition-colors duration-200">
                {c.status === "published" ? "Unpublish" : "Publish"}
              </button>
              {c.spots_taken === 0 && (
                <button data-testid={`camp-delete-${c.id}`} onClick={() => remove(c)}
                  className="text-[10px] uppercase tracking-widest border border-white/15 text-zinc-400 px-3 py-2 hover:border-[#ff3b30] hover:text-[#ff3b30] transition-colors duration-200 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PayoutsPanel = () => {
  const [data, setData] = useState(null);
  const [method, setMethod] = useState("");
  const [details, setDetails] = useState("");

  const load = () => api.get("/coach/payouts").then((r) => {
    setData(r.data); setMethod(r.data.payout_method || ""); setDetails(r.data.payout_details || "");
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/coaches/me", { payout_method: method, payout_details: details });
      toast.success("Payout details saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <p className="mt-8 text-sm text-zinc-500">Loading payouts…</p>;

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-white/10 bg-[#121212] p-6 space-y-4">
        <h2 className="display text-xl uppercase tracking-tight font-bold">Where should we pay you?</h2>
        <p className="text-xs text-zinc-500 leading-relaxed">Payout statements are generated automatically every Monday from your completed sessions, minus the platform fee.</p>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Method</span>
          <select data-testid="payout-method-select" className={input} value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">Choose…</option>
            {["Bank transfer (ACH)", "Interac e-Transfer", "PayPal", "Cheque"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-zinc-500">Account / email for payouts</span>
          <input data-testid="payout-details-input" className={input} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="e.g. coach@email.com or ****1234" /></label>
        <button data-testid="payout-save-btn" onClick={save}
          className="bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#00b3cc] transition-colors duration-200">Save payout details</button>
      </div>

      <div className="space-y-3">
        {data.batches.length === 0 && <p data-testid="coach-no-payouts" className="border border-white/10 p-10 text-sm text-zinc-500">No payout statements yet. They appear once sessions are completed.</p>}
        {data.batches.map((b) => (
          <div key={b.id} data-testid={`payout-batch-${b.id}`} className="border border-white/10 bg-[#121212] p-5 flex items-center justify-between">
            <div>
              <p className="display text-2xl font-bold text-[#00e5ff] leading-none">{money(b.amount)}</p>
              <p className="text-[11px] text-zinc-500 mt-2">{b.bookings} session(s) · {new Date(b.created_at).toLocaleDateString()}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-widest border px-3 py-1.5 ${b.status === "paid" ? "border-emerald-500/50 text-emerald-400" : "border-amber-500/50 text-amber-400"}`}>{b.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
